import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faScaleBalanced,
  faRightFromBracket,
  faCertificate,
  faClock,
  faUserClock,
  faCalendarCheck,
  faMoneyBill,
  faShirt,
  faLeaf,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import { useSection } from "../data/content";
import { resolveIcon } from "../data/iconMap";
import "./AboutUs.css";

export const aboutSectionsDefault = [
  {
    id: "rules",
    icon: faScaleBalanced,
    title: "Rules & Regulations of the College",
    type: "list",
    items: [
      "The Discipline of college is to be strictly observed. For breach of discipline a student may be fined or expelled from the college. No refund of Fee will be allowed.",
      "Any Harsh or insulting language used in the office or written in the letter may affect the admission of the student.",
      "Student must arrange their eligibility while taking admission to a particular class.",
      "If student fails to take examination for one reason or other, the college will not entertain any claim for the refund of the fees.",
      "In all matters, the decision of the principal will be final.",
      "Any dispute arising out of the transaction will be subject to Tarn-Taran Jurisdiction only.",
      "The mode of payment of fees should be strictly followed. If any installment is not paid in time, the name of the student will be struck off.",
      "Students are not permitted to possess such books which excite basic instincts.",
      "College will not be responsible in case of delay or postponement of the examination up to any extent by PNRC, BFUHS & I.K Gujral Punjab Technical University.",
    ],
  },
  {
    id: "withdrawal",
    icon: faRightFromBracket,
    title: "Withdrawal / Dismissal",
    type: "text",
    body:
      "If any student decides to withdraw voluntarily from the Institute after the preliminary period or any time before completing the course, her deposited money will not be refunded. The College reserves the right to dismiss any student at any time whose work or conduct is found unsatisfactory during the period of training.",
  },
  {
    id: "certificate",
    icon: faCertificate,
    title: "Award of Certificate",
    type: "text",
    body:
      "A Degree / Certificate (Diploma) is issued to every successful candidate by the body that recognises their course — the Punjab Nurses Registration Council and Indian Nursing Council for nursing programmes, Baba Farid University of Health Sciences of Health Sciences for B.Sc Nursing, and I.K. Gujral Punjab Technical University for allied healthcare and management degrees. Nursing students are eligible for registration with the Nurses and Midwifery Council of any state in India, which enables them to practise as an 'A' Grade Registered Nurse and Midwife.",
  },
  {
    id: "working-hours",
    icon: faClock,
    title: "Working Hours",
    type: "list",
    items: [
      "During Winter : 9.00 a.m. to 3.30 p.m.",
      "During Summer : 9.00 a.m. to 3.30 p.m.",
    ],
  },
  {
    id: "duty-hours",
    icon: faUserClock,
    title: "Duty Hours",
    type: "text",
    body: "Duty will be allotted according to hours of practical in the clinical area.",
  },
  {
    id: "attendance",
    icon: faCalendarCheck,
    title: "Attendance",
    type: "text",
    body:
      "All the students are required to be punctual on duty and in the classroom. They are not permitted to miss classes without genuine reason and without prior permission from the class teacher and the principal.",
  },
  {
    id: "fees",
    icon: faMoneyBill,
    title: "Fees and Dues",
    type: "text",
    body: "Fees will be charged as per Government directions from time to time.",
  },
  {
    id: "uniform",
    icon: faShirt,
    title: "Uniform and Books",
    type: "text",
    body:
      "The student will be required to wear approved uniform while on duty and in the classroom and hospital. The expenditure on uniform will be borne by the students.",
  },
];

export const aboutQuickFactsDefault = [
  { icon: faClock, label: "Class hours", value: "9:00 a.m. – 3:00 p.m." },
  {
    icon: faCertificate,
    label: "Certification",
    value:
      "PNRC · INC · Guru Nanak Dev University · BFUHS · I.K Gujral Punjab Technical University",
  },
  { icon: faCalendarCheck, label: "Attendance", value: "Punctuality is mandatory" },
  { icon: faScaleBalanced, label: "Jurisdiction", value: "Tarn-Taran only" },
];

const AboutUs = () => {
  // CMS-managed with the bundled data as verbatim fallback (identical until edited).
  const rawSections = useSection("aboutSections", aboutSectionsDefault);
  const sections = rawSections.map((s, i) => (s.id ? s : { ...s, id: `about-${i}` }));
  const quickFacts = useSection("aboutQuickFacts", aboutQuickFactsDefault);
  const [active, setActive] = useState(sections[0].id);
  const blockRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-24% 0px -68% 0px", threshold: 0 }
    );
    Object.values(blockRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id) => {
    const el = blockRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <section className="page-hero">
        <span className="blob" aria-hidden="true" />
        <div className="container page-hero-inner">
          <span className="page-hero-icon">
            <FontAwesomeIcon icon={faBookOpen} />
          </span>
          <p className="eyebrow">Student handbook</p>
          <h1 className="page-hero-title">Rules &amp; Regulations</h1>
          <p className="page-hero-lead">
            What's expected of you at Sri Guru Arjun Dev Ji Group of Institutes —
            read it before you join, and keep it handy through your course.
          </p>
        </div>
      </section>

      <section className="handbook">
        <div className="container">
          <div className="qfacts">
            {quickFacts.map((f) => (
              <div className="qfact" key={f.label}>
                <span className="qfact-icon">
                  <FontAwesomeIcon icon={resolveIcon(f.icon)} />
                </span>
                <div>
                  <span className="qfact-label">{f.label}</span>
                  <span className="qfact-value">{f.value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="handbook-grid">
            <aside className="handbook-index" aria-label="Contents">
              <p className="idx-title">On this page</p>
              <nav>
                {sections.map((s, i) => (
                  <button
                    key={s.id}
                    className={`idx-link ${active === s.id ? "is-active" : ""}`}
                    onClick={() => jumpTo(s.id)}
                  >
                    <span className="idx-num">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </button>
                ))}
              </nav>
            </aside>

            <div className="handbook-body">
              {sections.map((s, i) => (
                <article
                  key={s.id}
                  id={s.id}
                  ref={(el) => (blockRefs.current[s.id] = el)}
                  className="rule-block"
                >
                  <header className="rule-block-head">
                    <span className="rule-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="rule-icon">
                      <FontAwesomeIcon icon={resolveIcon(s.icon)} />
                    </span>
                    <h2 className="rule-title">{s.title}</h2>
                  </header>
                  {s.type === "list" ? (
                    <ol className="rule-list">
                      {s.items.map((item, idx) => (
                        <li key={idx}>
                          <FontAwesomeIcon icon={faLeaf} className="rule-bullet" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="rule-text">{s.body}</p>
                  )}
                </article>
              ))}

              <div className="handbook-cta">
                <div>
                  <h3 className="handbook-cta-title">Still have a question?</h3>
                  <p>Our office is glad to walk you through anything before you apply.</p>
                </div>
                <a href="/admissions" className="btn btn--primary">
                  Talk to admissions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUs;
